/**
 * NativePlanNotice — shown INSTEAD of any pricing/plan page when the
 * app runs inside the native iOS/Android shell. App Store guideline
 * 3.1.1 forbids showing purchase CTAs / prices for digital services
 * that aren't offered via In-App Purchase, so native builds render a
 * plain, price-free feature overview with no purchase path.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon as CheckCircle, EnvelopeSimpleIcon as EnvelopeSimple, ArrowSquareOutIcon as ArrowSquareOut, CaretLeftIcon as CaretLeft } from "@phosphor-icons/react";
import useIsUsAccount from "@/hooks/useIsUsAccount";
import IapPaywall from "@/components/IapPaywall";

const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "hello@armstrongk9registry.com";

const FEATURES = [
  "Dog & litter registration records",
  "Pedigree certificates & lineage trees",
  "Heat-cycle tracker with breeding-window reminders",
  "Marketplace browsing & breeder messaging",
  "Dog Log care hub — meals, walks, weights, vet notes",
  "Events, community feed, and kennel profiles",
];

export default function NativePlanNotice() {
  // External payment link-outs are US-storefront-only (3.1.1). Non-US
  // accounts see the support email path instead.
  const isUS = useIsUsAccount();
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/dashboard");
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink px-6 pt-8 pb-16" data-testid="native-plan-notice">
      <div className="max-w-md mx-auto">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#6B6B6B] hover:text-ink transition-colors mb-8 -ml-1"
          data-testid="native-plan-back-button"
        >
          <CaretLeft size={16} weight="bold" />
          Back
        </button>
        <h1 className="font-serif text-3xl mb-3">Your account</h1>
        <p className="text-[15px] text-[#6B6B6B] leading-relaxed mb-8">
          Sign in to access everything included with your Armstrong K9
          Registry account.
        </p>
        <ul className="space-y-3">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-[14px]">
              <CheckCircle size={18} weight="duotone" className="text-[#8A6A00] flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {/* US-storefront link-out allowance: buttons that open the default
            browser for payment/account management are permitted (per App
            Review's own 3.1.1 guidance for US storefront apps). Hidden for
            non-US accounts, where the allowance does not apply. */}
        {isUS && (
        <div className="mt-10 pt-6 border-t border-[#EAEAEA]">
          <button
            type="button"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/billing`, "_blank")}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink hover:bg-crimson text-white text-[14px] font-semibold min-h-12 px-5 py-3 text-center transition-colors"
            data-testid="native-plan-manage-link"
          >
            View plans &amp; subscribe
            <ArrowSquareOut size={16} weight="bold" />
          </button>
          <p className="text-[11px] text-[#9CA3AF] mt-2 text-center">
            Checkout opens in your browser — purchases are completed on
            armstrongk9registry.com
          </p>
        </div>
        )}
        <div className="mt-8">
          <IapPaywall />
        </div>
        <div className="mt-6">
          <p className="text-[13px] text-[#6B6B6B] mb-2">
            Questions about your account or billing?
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=Account%20help`}
            className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink underline underline-offset-4"
            data-testid="native-plan-support-link"
          >
            <EnvelopeSimple size={16} weight="duotone" />
            {SUPPORT_EMAIL}
          </a>
          <p className="text-[11px] text-[#9CA3AF] mt-2">
            Our team typically replies within one business day.
          </p>
        </div>
        <div className="mt-8 pt-5 border-t border-[#EAEAEA]">
          <button
            type="button"
            onClick={() => navigate("/settings/privacy")}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6B6B6B] hover:text-crimson underline underline-offset-4 transition-colors"
            data-testid="native-plan-delete-account-link"
          >
            Manage my data / Delete my account
          </button>
        </div>
      </div>
    </div>
  );
}
